import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CoreProvider } from '../core.provider';
import { Mutex } from '../auth/mutex';
import { EventDto, GameDto, PRESIGNED_CLOUD_VIDEO_EXPIRATION_TIME_MS } from './games.service';

export type VideoCacheStatus = 'pending' | 'local-downloading' | 'cloud-downloading' | 'cached' | 'failed';

/** Where a clip is being pulled from: the camera device over the local network, or cloud storage. */
type DownloadSource = { url: string, origin: 'local' | 'cloud' };

/** Suffix of the folder holding the clips a bench device downloaded for a live session. */
const CACHE_FOLDER_SUFFIX = '_cache';
/** A clip that failed to download is not retried until this long has passed. */
const FAILED_RETRY_BACKOFF_MS = 30 * 1000;
/** Partially written files carry this suffix so a half-download is never mistaken for a cached clip. */
const PARTIAL_FILE_SUFFIX = '.part';

/**
 * Keeps a local copy of the game clips on a **native bench device during a live session**, so that
 * reviewing a clip does not depend on the arena WiFi holding up.
 *
 * The cache is deliberately scoped to the live session: it is wiped when the session is torn down,
 * which is what makes post-game review fall back to cloud video without the URL resolution having to
 * know anything about the current role or game status — it just finds nothing on disk.
 *
 * On web there is no filesystem, so every lookup reports "not downloaded" and nothing is stored.
 */
@Injectable({
  providedIn: 'root'
})
export class VideoCacheService {

  private core?: CoreProvider;

  /** Per-event download status, keyed by `EventDto.localId`. Exposed for UI affordances. */
  public cacheStatus$ = new BehaviorSubject<Record<string, VideoCacheStatus>>({});

  private downloadInterval?: any;
  private activeGame?: GameDto;
  private failedAt: Record<string, number> = {};

  async initCore(core: CoreProvider) {
    this.core = core;
    // A session that died with the app (crash, force quit) leaves its folder behind. Nothing outlives
    // the session it belongs to, so anything still here on boot is garbage.
    await this.sweepOrphanCaches();
  }

  /** Only native devices cache clips; a PWA behaves as if nothing were ever downloaded. */
  public get enabled(): boolean {
    return !!this.core && (this.core.isAndroid || this.core.isIOS);
  }

  public cacheFolder(gameLocalId: string): string {
    return `${gameLocalId}${CACHE_FOLDER_SUFFIX}`;
  }

  /**
   * Events that reached this device only through the cloud have no `videoUri`
   * (see `mapCloudPbpToLocalPbp`), so fall back to a name derived from the event itself.
   */
  public cacheFileName(event: EventDto): string {
    if (event.videoUri) return event.videoUri;
    const extension = this.extensionFromUrl(event.lastPresignedCloudVideoUrl) ?? 'mp4';
    return `${event.localId}.${extension}`;
  }

  private extensionFromUrl(url?: string): string | undefined {
    if (!url) return undefined;
    try {
      const fileName = new URL(url).pathname.split('/').pop() ?? '';
      const extension = fileName.includes('.') ? fileName.split('.').pop() : undefined;
      // Guard against query-ish garbage sneaking in as an "extension"
      return extension && /^[a-z0-9]{2,5}$/i.test(extension) ? extension.toLowerCase() : undefined;
    } catch (error) {
      return undefined;
    }
  }

  //#region Local availability
  /**
   * Filesystem path of the clip if this device already holds it, otherwise `undefined`.
   * Two folders can hold it: the game folder itself (clips this device recorded as the camera) and
   * the live-session cache (clips this device downloaded as a bench).
   */
  public async localVideoPath(event: EventDto, game: GameDto): Promise<string | undefined> {
    if (!this.enabled) return undefined;

    const candidates: { folder: string, fileName: string }[] = [];
    if (event.videoUri) candidates.push({ folder: game.localId, fileName: event.videoUri }); // Camera candidate (direct video uri present at filesytem)
    candidates.push({ folder: this.cacheFolder(game.localId), fileName: this.cacheFileName(event) }); // Bench candidate (cache folder if already downloaded)

    for (const candidate of candidates) {
      if (await this.core!.ldFilesService.fileExists([candidate.folder], candidate.fileName)) {
        return this.core!.ldFilesService.getDataDirectory() + this.core!.ldFilesService.baseVideoPath +
          '/' + candidate.folder + '/' + candidate.fileName;
      }
    }
    return undefined;
  }

  /** Same as `localVideoPath`, but as a URL the webview can feed to a `<video>` element. */
  public async localVideoUrl(event: EventDto, game: GameDto): Promise<string | undefined> {
    const path = await this.localVideoPath(event, game);
    return path ? this.core!.ldFilesService.toWebViewUrl(path) : undefined;
  }
  //#endregion

  //#region Session lifecycle
  /**
   * Both entry points serialise on the same lock. `resetSubjects()` fires `stop()` without awaiting
   * it and `startNewGame()` follows immediately with `start()`; without the lock the in-flight folder
   * deletion could land after the new folder was created and wipe the fresh session's cache.
   */
  public async start(game: GameDto) {
    if (!this.enabled) return;
    const { unlock } = await Mutex.getLock('bench-video-cache-lifecycle').lock();
    try {
      if (this.activeGame?.localId === game.localId && this.downloadInterval) return;

      await this.teardown();
      this.activeGame = game;
      await this.core!.ldFilesService.ensureDirectoryExists([this.cacheFolder(game.localId)]);
      console.log('Bench video cache started for game', game.localId);

      this.downloadInterval = setInterval(() => this.downloadTick(), 2000);
    } catch (error) {
      console.error('Error starting bench video cache', error);
    } finally {
      unlock(null);
    }
  }

  public async stop() {
    const { unlock } = await Mutex.getLock('bench-video-cache-lifecycle').lock();
    try {
      await this.teardown();
    } catch (error) {
      console.error('Error stopping bench video cache', error);
    } finally {
      unlock(null);
    }
  }

  private async teardown() {
    if (this.downloadInterval) {
      clearInterval(this.downloadInterval);
      this.downloadInterval = undefined;
    }
    const game = this.activeGame;
    this.activeGame = undefined;
    this.failedAt = {};
    this.cacheStatus$.next({});

    if (game && this.enabled) {
      // Dropping the cache is what makes a later post-game visit resolve to cloud video
      await this.core!.ldFilesService.deleteDirectory([this.cacheFolder(game.localId)]);
    }
  }

  private async sweepOrphanCaches() {
    if (!this.enabled) return;
    try {
      const folders = await this.core!.ldFilesService.listDirectoryNames();
      for (const folder of folders.filter(name => name.endsWith(CACHE_FOLDER_SUFFIX))) {
        console.log('Removing orphan bench video cache', folder);
        await this.core!.ldFilesService.deleteDirectory([folder]);
      }
    } catch (error) {
      console.error('Error sweeping orphan video caches', error);
    }
  }
  //#endregion

  //#region Download loop
  private setStatus(eventLocalId: string, status: VideoCacheStatus) {
    this.cacheStatus$.next({ ...this.cacheStatus$.getValue(), [eventLocalId]: status });
  }

  /** Ordered download sources for a clip: the camera device over the local network, then cloud. */
  private downloadSources(event: EventDto, game: GameDto): DownloadSource[] {
    const gameTracker = this.core!.gameTrackerService;
    const sources: DownloadSource[] = [];

    if (event.videoUri && gameTracker.cameraDeviceUrl && gameTracker.localConnectionStatus$.getValue() !== 'disconnected') {
      sources.push({ url: `${gameTracker.cameraDeviceUrl}/static/${game.localId}/${event.videoUri}`, origin: 'local' });
    }
    if (event.cloudVideoUploaded && event.lastPresignedCloudVideoUrl &&
      (event.lastPresignedCloudVideoTimestamp ?? 0) + PRESIGNED_CLOUD_VIDEO_EXPIRATION_TIME_MS >= Date.now()) {
      sources.push({ url: event.lastPresignedCloudVideoUrl, origin: 'cloud' });
    }
    return sources;
  }

  private async downloadTick() {
    if (!this.enabled || !this.activeGame) return;

    const game = this.activeGame;
    const gameTracker = this.core!.gameTrackerService;
    // Caching is a live-session behaviour only
    if (gameTracker.connectionMode !== 'bench' || gameTracker.gameDto?.game.gameStatus !== 'live') return;

    const { unlock, error } = await Mutex.getLock('bench-video-cache-download').lock(true, true);
    if ((error as Error & { customCode?: string })?.customCode === 'ALREADY_WAITING') return;
    try {
      const statuses = this.cacheStatus$.getValue();
      const isDownloading = (status?: VideoCacheStatus) => status === 'local-downloading' || status === 'cloud-downloading';
      // Newest first — that is what the bench reviews most
      const pending = gameTracker.gameEvents$.getValue()
        .filter(event => statuses[event.localId] !== 'cached' && !isDownloading(statuses[event.localId]))
        .filter(event => statuses[event.localId] !== 'failed' || Date.now() - (this.failedAt[event.localId] ?? 0) > FAILED_RETRY_BACKOFF_MS)
        .sort((a, b) => b.orderId - a.orderId);

      for (const event of pending) {
        // The session may have been torn down while we awaited
        if (this.activeGame?.localId !== game.localId) return;

        const fileName = this.cacheFileName(event);
        if (await this.core!.ldFilesService.fileExists([this.cacheFolder(game.localId)], fileName)) {
          this.setStatus(event.localId, 'cached');
          continue;
        }

        const sources = this.downloadSources(event, game);
        if (sources.length === 0) {
          // Nothing serving this clip yet (not uploaded, camera unreachable) — retry on a later tick
          this.setStatus(event.localId, 'pending');
          continue;
        }

        // downloadClip reports the status per attempt, so the indicator shows the source actually in use
        const downloaded = await this.downloadClip(event.localId, game, fileName, sources);
        // The session may have been torn down mid-download; do not write into the next session's state
        if (this.activeGame?.localId !== game.localId) return;
        if (downloaded) {
          this.setStatus(event.localId, 'cached');
        } else {
          this.failedAt[event.localId] = Date.now();
          this.setStatus(event.localId, 'failed');
        }
        // One clip per tick keeps the download off the polling and playback traffic
        return;
      }
    } catch (error) {
      console.error('Error on bench video cache tick', error);
    } finally {
      unlock(null);
    }
  }

  /** Downloads to a `.part` file and only then renames, so a killed app never leaves a truncated clip. */
  private async downloadClip(eventLocalId: string, game: GameDto, fileName: string, sources: DownloadSource[]): Promise<boolean> {
    const folder = this.cacheFolder(game.localId);
    const partialFileName = fileName + PARTIAL_FILE_SUFFIX;

    for (const source of sources) {
      this.setStatus(eventLocalId, source.origin === 'local' ? 'local-downloading' : 'cloud-downloading');
      try {
        await this.core!.ldFilesService.downloadVideoFromUrl(source.url, [folder], partialFileName);
        await this.core!.ldFilesService.renameFile([folder], partialFileName, fileName);
        console.log('Cached clip', fileName, 'from', source.origin, source.url);
        return true;
      } catch (error) {
        console.error('Error caching clip', fileName, 'from', source.origin, source.url, error);
      }
    }
    return false;
  }
  //#endregion

}
